import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useDeepCompareEffect } from 'react-use';
import NotifyUsersSettings from '../../../../components/init-form/settings/notify-users-settings';
import NotificationStatisticsSettings from '../../../../components/init-form/settings/notification-statistics-settings';
import { DATE_FORMAT } from '../../../../constants';
import { notificationOptions } from '../../../../constants/forms/options';
import { STATIC_ROUTES } from '../../../../constants/routes';
import { useAuth } from '../../../../container/authContext';
import { runTaskService } from '../../../../services/tasks';
import { isResponseOk } from '../../../../utility/isResponseOk';
import { cloneFormState } from '../../../../utility/forms/clone-form-state';
import SubmissionSettingsSystem from '../../components/forms/SubmissionSettingsSystem';
import SubmissionSettingsContacts from '../../components/forms/SubmissionSettingsContacts';
import SubmissionSettingsUnregistered from '../../components/forms/SubmissionSettingsUnregistered';
import RecipientSettings from '../../components/forms/RecipientsSettings';
import TaskFrequencySettings from '../../components/forms/TaskFrequencySettings';
import TaskScheduledInitiationSettings from '../../components/forms/TaskScheduledInitiationSettings';

const REMINDER_FIELDS = [
  'should_notify_users_to_fill_the_form',
  'notify_users_to_fill_the_form_notification_type',
  'notify_users_to_fill_the_form_notification_text',
  'notify_users_to_fill_the_form_repeat',
  'notify_users_to_fill_the_form_period',
  'notify_users_to_fill_the_form_sending_week_days',
  'notify_users_to_fill_the_form_sending_month_days',
  'notify_users_to_fill_the_form_sending_time',
  'notify_users_to_fill_the_form_stop_type',
  'notify_users_to_fill_the_form_stop_period',
  'notify_users_to_fill_the_form_stop_frequency',
  'notify_users_to_fill_the_form_stop_date',
];

// Fallback for forms saved without submitter settings; every task in the product
// assigns its forms to system users.
const DEFAULT_SUBMITTERS_TYPE = 'user';

// Submitters are notified through their own channel preference unless the
// initiator explicitly picks Email or SMS.
const USER_DEFAULT_NOTIFICATION_OPTION = notificationOptions.find((option) => option.value === 'USER_DEFAULT');

// Held as react-select options and date objects while the settings are being filled in,
// so the raw state never goes to the API -- getFrequencyPayload sends it instead.
const FREQUENCY_FIELDS = ['frequency'];

// Same reason as the frequency above: the date is a date object and the time a
// react-select option until getScheduledInitiationPayload turns them into the payload.
const SCHEDULED_INITIATION_FIELDS = ['scheduled_initiation'];

// The two scheduling settings are alternatives -- a task either recurs or goes out once
// at a chosen moment -- so answering Yes to one answers No to the other and drops
// whatever was filled in underneath it.
const SCHEDULE_ALTERNATIVES = {
  will_have_frequency: { setting: 'will_have_scheduled_initiation', settings: 'scheduled_initiation' },
  will_have_scheduled_initiation: { setting: 'will_have_frequency', settings: 'frequency' },
};

const STATISTIC_FIELDS = [
  'should_notify_about_statistics',
  'statistic_notification_repeat',
  'statistic_notification_period',
  'statistic_notification_sending_week_days',
  'statistic_notification_sending_month_days',
  'statistic_notification_sending_time',
  'statistic_notification_stop_type',
  'statistic_notification_stop_period',
  'statistic_notification_stop_frequency',
  'statistic_notification_stop_date',
];

const getStatisticNotificationSettingsPayload = (formData) => {
  if (!formData.should_notify_about_statistics) {
    return { should_notify_about_statistics: false };
  }

  const settings = {
    should_notify_about_statistics: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    statistic_notification_repeat: formData.statistic_notification_repeat?.value || null,
    statistic_notification_period: formData.statistic_notification_period?.value || null,
    statistic_notification_sending_week_days: formData.statistic_notification_sending_week_days,
    statistic_notification_sending_month_days: formData.statistic_notification_sending_month_days.map((item) => item.day),
    statistic_notification_sending_time: formData.statistic_notification_sending_time?.value || null,
    statistic_notification_stop_type: formData.statistic_notification_stop_type?.value || null,
  };

  if (formData.statistic_notification_stop_type?.value === 'interval') {
    settings.statistic_notification_stop_period = formData.statistic_notification_stop_period?.value || null;
    settings.statistic_notification_stop_frequency = formData.statistic_notification_stop_frequency?.value || null;
  } else {
    settings.statistic_notification_stop_date = formData.statistic_notification_stop_date
      ? dayjs(formData.statistic_notification_stop_date).format(DATE_FORMAT)
      : null;
  }

  return settings;
};

// The frequency of a task drives the whole set of its forms, so it is sent as one
// setting of the initiation rather than per form. Shapes match the form-level
// frequency the API already knows how to schedule.
const getFrequencyPayload = (formData) => {
  const frequency = formData.frequency || {};
  const settings = {
    frequency_type: frequency.frequency_type?.value || null,
    time: frequency.time?.value || null,
  };

  if (frequency.frequency_type?.value === 'specific_dates') {
    settings.specific_dates = (frequency.specific_dates || []).map((date) => dayjs(date.toDate()).format(DATE_FORMAT));

    return settings;
  }

  settings.first_initiation_day = frequency.first_initiation_day ? dayjs(frequency.first_initiation_day).format(DATE_FORMAT) : null;
  settings.last_initiation_day = frequency.last_initiation_day ? dayjs(frequency.last_initiation_day).format(DATE_FORMAT) : null;
  settings.type = frequency.type?.value || null;
  settings.times = frequency.times?.value || null;

  // The week days and months are held as a checkbox per position; the API expects the
  // positions that are checked -- week days from 0 (Sunday), months from 1 (January).
  if (frequency.type?.value === 'weekly') {
    settings.week_days = (frequency.week_days || [])
      .map((isChecked, index) => (isChecked ? index : null))
      .filter((weekDay) => weekDay !== null);
  }

  if (frequency.type?.value === 'yearly') {
    settings.months = (frequency.months || []).map((isChecked, index) => (isChecked ? index + 1 : null)).filter((month) => month !== null);
  }

  if (['monthly', 'yearly'].includes(frequency.type?.value)) {
    settings.month_day = (frequency.month_day || []).map((date) => date.day);
  }

  return settings;
};

// A task initiated once at a chosen moment has nothing recurring to describe, so the
// settings are only the date and the hour, in the formats the API parses them with.
const getScheduledInitiationPayload = (formData) => {
  const scheduledInitiation = formData.scheduled_initiation || {};

  return {
    date: scheduledInitiation.date ? dayjs(scheduledInitiation.date).format(DATE_FORMAT) : null,
    time: scheduledInitiation.time?.value || null,
  };
};

const getNotificationSettingsPayload = (formData) => {
  if (!formData.should_notify_users_to_fill_the_form) {
    return { should_notify_users_to_fill_the_form: false };
  }

  const settings = {
    should_notify_users_to_fill_the_form: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    notify_users_to_fill_the_form_notification_type: formData.notify_users_to_fill_the_form_notification_type?.value || null,
    notify_users_to_fill_the_form_notification_text: formData.notify_users_to_fill_the_form_notification_text || '',
    notify_users_to_fill_the_form_repeat: formData.notify_users_to_fill_the_form_repeat?.value || null,
    notify_users_to_fill_the_form_period: formData.notify_users_to_fill_the_form_period?.value || null,
    notify_users_to_fill_the_form_sending_week_days: formData.notify_users_to_fill_the_form_sending_week_days,
    notify_users_to_fill_the_form_sending_month_days: formData.notify_users_to_fill_the_form_sending_month_days.map((item) => item.day),
    notify_users_to_fill_the_form_sending_time: formData.notify_users_to_fill_the_form_sending_time?.value || null,
    notify_users_to_fill_the_form_stop_type: formData.notify_users_to_fill_the_form_stop_type?.value || null,
  };

  if (formData.notify_users_to_fill_the_form_stop_type?.value === 'interval') {
    settings.notify_users_to_fill_the_form_stop_period = formData.notify_users_to_fill_the_form_stop_period?.value || null;
    settings.notify_users_to_fill_the_form_stop_frequency = formData.notify_users_to_fill_the_form_stop_frequency?.value || null;
  } else {
    settings.notify_users_to_fill_the_form_stop_date = formData.notify_users_to_fill_the_form_stop_date
      ? dayjs(formData.notify_users_to_fill_the_form_stop_date).format(DATE_FORMAT)
      : null;
  }

  return settings;
};

const TaskSettingsTab = ({ task }) => {
  // Forms whose settings were never filled in come back with an empty
  // submission_settings, and every consumer below reaches straight into
  // submitters.* -- so the task is normalised once here and the settings
  // components receive the normalised copy instead of the raw one.
  const normalizedTask = useMemo(
    () => ({
      ...task,
      task_forms: (task.task_forms || []).map((taskForm) => ({
        ...taskForm,
        submission_settings: {
          ...taskForm.submission_settings,
          submitters: {
            submitters_type: DEFAULT_SUBMITTERS_TYPE,
            facilities: [],
            user_groups: [],
            individual_users: [],
            contacts: [],
            units: [],
            ...taskForm.submission_settings?.submitters,
          },
        },
        recipient_settings: {
          facilities: [],
          user_groups: [],
          individual_users: [],
          ...taskForm.recipient_settings,
        },
      })),
    }),
    [task],
  );

  const [firstTaskForm] = normalizedTask.task_forms;
  const submittersType = firstTaskForm?.submission_settings?.submitters?.submitters_type;
  const canSelectRecipients = firstTaskForm?.recipient_settings?.can_initiator_select_recipients;
  const hasStrictOrderOfSubmissions = firstTaskForm?.submission_settings?.has_strict_order_of_submissions;
  // The Statistics report covers the whole task, so it is only offered when every
  // form of the task is added to the statistics.
  const canNotifyAboutStatistics =
    normalizedTask.task_forms.length > 0 &&
    normalizedTask.task_forms.every((taskForm) => !!taskForm.submission_settings?.include_in_statistics);
  const [isRunningTask, setIsRunningTask] = useState(false);
  const { user } = useAuth();
  const {
    company: { id: companyID },
  } = user;
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});

  useDeepCompareEffect(() => {
    const initialFormData = {
      notify_recipients: false,
      recipients_notification_type: { value: 'EMAIL', label: 'Email' },
      recipients_notification_text: '',
      notify_submitters: false,
      submitters_notification_type: USER_DEFAULT_NOTIFICATION_OPTION,
      submitters_notification_text: '',
      // Answered Yes by default: whoever ran the task is expected to want to hear that
      // a submitter finished it, so the initiator opts out rather than in.
      notify_initiator: true,
      initiator_notification_type: USER_DEFAULT_NOTIFICATION_OPTION,
      initiator_notification_text: '',

      will_have_frequency: false,
      frequency: {},

      will_have_scheduled_initiation: false,
      scheduled_initiation: {},

      should_notify_users_to_fill_the_form: false,
      notify_users_to_fill_the_form_notification_type: { value: 'EMAIL', label: 'Email' },
      notify_users_to_fill_the_form_notification_text: '',
      notify_users_to_fill_the_form_repeat: null,
      notify_users_to_fill_the_form_period: null,
      notify_users_to_fill_the_form_sending_week_days: [],
      notify_users_to_fill_the_form_sending_month_days: [],
      notify_users_to_fill_the_form_sending_time: null,
      notify_users_to_fill_the_form_stop_type: null,
      notify_users_to_fill_the_form_stop_period: null,
      notify_users_to_fill_the_form_stop_frequency: null,
      notify_users_to_fill_the_form_stop_date: null,
    };

    if (canNotifyAboutStatistics) {
      initialFormData.should_notify_about_statistics = false;
      initialFormData.statistic_notification_repeat = null;
      initialFormData.statistic_notification_period = null;
      initialFormData.statistic_notification_sending_week_days = [];
      initialFormData.statistic_notification_sending_month_days = [];
      initialFormData.statistic_notification_sending_time = null;
      initialFormData.statistic_notification_stop_type = null;
      initialFormData.statistic_notification_stop_period = null;
      initialFormData.statistic_notification_stop_frequency = null;
      initialFormData.statistic_notification_stop_date = null;
    }

    if (canSelectRecipients) {
      initialFormData.recipients = {
        facilities: [],
        user_groups: [],
        individual_users: [],
      };
    }

    if (!formData?.submitters) {
      switch (submittersType) {
        case 'user': {
          initialFormData.submitters = {
            facilities: [],
            user_groups: [],
            individual_users: [],
          };

          if (hasStrictOrderOfSubmissions) {
            initialFormData.submitters.strict_order = [
              {
                step: 0,
              },
              {
                step: 1,
              },
            ];
          }

          break;
        }
        case 'contact': {
          initialFormData.submitters = {
            contact_person_submitter_type: null,
            contacts: [],
            units: [],
          };
          break;
        }
        case 'unregistered': {
          initialFormData.submitters = {
            unregistered_submitters: [
              {
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
              },
            ],
          };
          initialFormData.notify_submitters = true;
          break;
        }
        default:
          break;
      }
    }

    setFormData(initialFormData);
  }, [task]);

  const handleFormChange = (key, value) => {
    setFormData((prevState) => {
      const newState = cloneFormState(prevState);
      _.set(newState, key, value);
      return newState;
    });
  };

  const handleScheduleChange = (key, value) => {
    const alternative = SCHEDULE_ALTERNATIVES[key];

    if (!alternative || value !== true) {
      handleFormChange(key, value);
      return;
    }

    setFormData((prevState) => {
      const newState = cloneFormState(prevState);
      _.set(newState, key, true);
      _.set(newState, alternative.setting, false);
      _.set(newState, alternative.settings, {});
      return newState;
    });
  };

  const handleRunTheTask = useCallback(async () => {
    try {
      setIsRunningTask(true);
      const body = {
        ..._.omit(formData, [...REMINDER_FIELDS, ...STATISTIC_FIELDS, ...FREQUENCY_FIELDS, ...SCHEDULED_INITIATION_FIELDS]),
        submitters_notification_type: formData.submitters_notification_type?.value,
        recipients_notification_type: formData.recipients_notification_type?.value,
        initiator_notification_type: formData.initiator_notification_type?.value,
        notification_settings: getNotificationSettingsPayload(formData),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        task_id: task.id,
      };

      if (formData.will_have_frequency) {
        body.frequency = getFrequencyPayload(formData);
      }

      if (formData.will_have_scheduled_initiation) {
        body.scheduled_initiation = getScheduledInitiationPayload(formData);
      }

      if (canNotifyAboutStatistics) {
        body.statistic_notification_settings = getStatisticNotificationSettingsPayload(formData);
      }

      switch (submittersType) {
        case 'user': {
          body.submitters = {
            facilities: formData.submitters.facilities.map((item) => item.value),
            user_groups: formData.submitters.user_groups.map((item) => item.value),
            individual_users: formData.submitters.individual_users.map((item) => item.value),
          };

          if (hasStrictOrderOfSubmissions) {
            body.submitters = {
              ...body.submitters,
              has_strict_order_of_submissions: true,
              strict_order: formData.submitters.strict_order,
            };
          }
          break;
        }
        case 'contact': {
          body.submitters = {
            contacts: formData.submitters.contacts.map((item) => item.value),
            units: formData.submitters.units.map((item) => item.value),
            contact_person_submitter_type: formData.submitters.contact_person_submitter_type,
          };
          break;
        }
        case 'unregistered':
          {
            body.submitters = formData.submitters;
          }
          break;
      }

      if (formData.recipients) {
        body.recipients = {
          facilities: formData.recipients.facilities.map((facility) => facility.value),
          user_groups: formData.recipients.user_groups.map((group) => group.value),
          individual_users: formData.recipients.individual_users.map((user) => user.value),
        };
      }

      const response = await runTaskService(task.id, body);

      if (isResponseOk(response)) {
        navigate(STATIC_ROUTES.TASKS);
        toast(`The task was run successfully`, {
          icon: () => <img src="/images/full-check-circle.svg" />,
          closeButton: ({ closeToast }) => (
            <button onClick={closeToast} className="btn btn-icon close-alert-btn">
              <img src="/images/x-icon.svg" />
            </button>
          ),
          className: 'task-run-alert',
          toastClassName: 'task-run-wrapper',
          autoClose: 5000,
          hideProgressBar: false,
          progress: undefined,
          theme: 'light',
          closeOnClick: true,
          type: 'success',
        });
      } else {
        setErrors(response.response.data);
      }
      setIsRunningTask(false);
    } catch (e) {
      console.log(e);
      setIsRunningTask(false);
    }
  }, [task.id, JSON.stringify(formData)]);

  return (
    <div className="task-details-tab-content">
      <div className="task-settings-tab">
        <div className="task-settings-tab-header">
          <h1>{task.name}</h1>
        </div>
        {submittersType === 'user' && formData && (
          <SubmissionSettingsSystem
            formData={formData}
            task={normalizedTask}
            handleFormChange={handleFormChange}
            companyID={companyID}
            formID={normalizedTask.task_forms[0]?.form_id}
            setFormData={setFormData}
            user={user}
            errors={errors}
            hasStrictOrderOfSubmissions={hasStrictOrderOfSubmissions}
          />
        )}
        {submittersType === 'contact' && formData && (
          <SubmissionSettingsContacts
            formData={formData}
            task={normalizedTask}
            handleFormChange={handleFormChange}
            companyID={companyID}
            user={user}
            errors={errors}
          />
        )}
        {submittersType === 'unregistered' && formData && (
          <SubmissionSettingsUnregistered
            formData={formData}
            task={normalizedTask}
            handleFormChange={handleFormChange}
            companyID={companyID}
            user={user}
            errors={errors}
          />
        )}
        {canSelectRecipients && formData && (
          <RecipientSettings
            formData={formData}
            task={normalizedTask}
            handleFormChange={handleFormChange}
            companyID={companyID}
            formID={normalizedTask.task_forms[0]?.form_id}
            setFormData={setFormData}
            user={user}
            errors={errors}
          />
        )}

        {formData && (
          <>
            <TaskScheduledInitiationSettings
              formData={formData}
              handleFormChange={handleScheduleChange}
              errors={errors?.scheduled_initiation || errors?.will_have_scheduled_initiation}
            />

            <TaskFrequencySettings formData={formData} handleFormChange={handleScheduleChange} errors={errors?.frequency} />

            {/* <div className="form-control-item-settings">
              <div className="form-settings-label">
                <label className="form-label form-label-big">Notify recipients when all forms in the task are completed</label>
                <span>1 notification = one submitter completed all forms.</span>
              </div>
              <div className="form-settings-inputs">
                <div className="d-flex">
                  <div className="form-control-radio">
                    <input
                      name="notify_recipients"
                      type="radio"
                      value="true"
                      onChange={() => handleFormChange('notify_recipients', true)}
                      checked={!!formData.notify_recipients}
                    />
                    <span>Yes</span>
                  </div>
                  <div className="form-control-radio">
                    <input
                      name="notify_recipients"
                      type="radio"
                      value="true"
                      onChange={() => handleFormChange('notify_recipients', false)}
                      checked={!formData.notify_recipients}
                    />
                    <span>No</span>
                  </div>
                </div>

                {formData.notify_recipients && (
                  <>
                    <div className="form-control-item-group">
                      <label className="form-label">Notification type</label>
                      <div className="select-multy-wrapper">
                        <Select
                          hideSelectedOptions={false}
                          isSearchable={false}
                          options={notificationOptions}
                          value={formData.recipients_notification_type}
                          onChange={(e) => handleFormChange('recipients_notification_type', e)}
                          className="form-control-select"
                          placeholder="Sms / Email"
                          classNamePrefix="react-select"
                        ></Select>
                      </div>
                      <span className="form-error">{errors?.recipients_notification_type && errors.recipients_notification_type[0]}</span>
                    </div>
                    <div className="form-control-item-group">
                      <label className="form-label">Notification text</label>
                      <textarea
                        onChange={(e) => handleFormChange('recipients_notification_text', e.target.value)}
                        value={formData.recipients_notification_text}
                        placeholder="Enter notification or message text that will be sent to recipients that the form is completely filled by all submitters"
                        className="form-control"
                      ></textarea>
                      <span className="form-error">{errors?.recipients_notification_text && errors.recipients_notification_text[0]}</span>
                    </div>
                  </>
                )}
              </div>
            </div> */}
            <div className="form-control-item-settings">
              <div className="form-settings-label">
                <label className="form-label form-label-big">Notify task initiator about completed task</label>
                <span>1 notification = one submitter completed all forms of the task.</span>
              </div>
              <div className="form-settings-inputs">
                <div className="d-flex">
                  <div className="form-control-radio">
                    <input
                      name="notify_initiator"
                      type="radio"
                      value="true"
                      onChange={() => handleFormChange('notify_initiator', true)}
                      checked={!!formData.notify_initiator}
                    />
                    <span>Yes</span>
                  </div>
                  <div className="form-control-radio">
                    <input
                      name="notify_initiator"
                      type="radio"
                      value="false"
                      onChange={() => handleFormChange('notify_initiator', false)}
                      checked={!formData.notify_initiator}
                    />
                    <span>No</span>
                  </div>
                </div>

                {formData.notify_initiator && (
                  <>
                    <div className="form-control-item-group">
                      <label className="form-label">Notification type</label>
                      <div className="select-multy-wrapper">
                        <Select
                          hideSelectedOptions={false}
                          isSearchable={false}
                          options={notificationOptions}
                          value={formData.initiator_notification_type}
                          onChange={(e) => handleFormChange('initiator_notification_type', e)}
                          className="form-control-select"
                          placeholder="Sms / Email"
                          classNamePrefix="react-select"
                        ></Select>
                      </div>
                      <span className="form-error">{errors?.initiator_notification_type && errors.initiator_notification_type[0]}</span>
                    </div>
                    <div className="form-control-item-group">
                      <label className="form-label">Notification text</label>
                      <textarea
                        onChange={(e) => handleFormChange('initiator_notification_text', e.target.value)}
                        value={formData.initiator_notification_text}
                        placeholder="Enter notification or message text that will be sent to you when a submitter completes all forms of the task"
                        className="form-control"
                      ></textarea>
                      <span className="form-error">{errors?.initiator_notification_text && errors.initiator_notification_text[0]}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {canNotifyAboutStatistics && (
              <NotificationStatisticsSettings
                notification_settings={formData}
                handelSettings={(value, key) => handleFormChange(key, value)}
                errors={errors?.statistic_notification_settings}
              />
            )}

            {!hasStrictOrderOfSubmissions && (
              <div className="form-control-item-settings">
                <div className="form-settings-label">
                  <label className="form-label form-label-big">Notify submitters about forms that are assigned to the submitter</label>
                </div>
                <div className="form-settings-inputs">
                  {submittersType !== 'unregistered' && (
                    <div className="d-flex">
                      <div className="form-control-radio">
                        <input
                          name="notify_submitters"
                          type="radio"
                          value="true"
                          onChange={() => handleFormChange('notify_submitters', true)}
                          checked={!!formData.notify_submitters}
                        />
                        <span>Yes</span>
                      </div>
                      <div className="form-control-radio">
                        <input
                          name="notify_submitters"
                          type="radio"
                          value="true"
                          onChange={() => handleFormChange('notify_submitters', false)}
                          checked={!formData.notify_submitters}
                        />
                        <span>No</span>
                      </div>
                    </div>
                  )}

                  {formData.notify_submitters && (
                    <>
                      <div className="form-control-item-group">
                        <label className="form-label">Notification type</label>
                        <div className="select-multy-wrapper">
                          <Select
                            hideSelectedOptions={false}
                            isSearchable={false}
                            options={notificationOptions}
                            value={formData.submitters_notification_type}
                            onChange={(e) => handleFormChange('submitters_notification_type', e)}
                            className="form-control-select"
                            placeholder="Sms / Email"
                            classNamePrefix="react-select"
                          ></Select>
                        </div>
                        <span className="form-error">{errors?.submitters_notification_type && errors.submitters_notification_type[0]}</span>
                      </div>
                      <div className="form-control-item-group">
                        <label className="form-label">Notification text</label>
                        <textarea
                          onChange={(e) => handleFormChange('submitters_notification_text', e.target.value)}
                          value={formData.submitters_notification_text}
                          placeholder="Enter notification or message text that will be sent to submitters that the task with forms were assigned"
                          className="form-control"
                        ></textarea>
                        <span className="form-error">{errors?.submitters_notification_text && errors.submitters_notification_text[0]}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <NotifyUsersSettings
              title="Remind users to fill out the forms"
              notificationTextPlaceholder="Enter notification or message text that will be sent to submitters who have not filled out all the forms of the task"
              notification_settings={formData}
              handelSettings={(value, key) => handleFormChange(key, value)}
              errors={errors?.notification_settings}
            />
          </>
        )}
      </div>
      <div className="form-control-actions">
        <button
          className="btn btn-outline"
          onClick={() => {
            navigate(STATIC_ROUTES.TASKS);
          }}
        >
          Close
        </button>
        <button className="btn" onClick={handleRunTheTask} disabled={isRunningTask}>
          {isRunningTask ? 'Running...' : 'Run the task'}
        </button>
      </div>
    </div>
  );
};

export default TaskSettingsTab;
